import SEO from '@/components/SEO';
import {
  Sparkles,
  ArrowUpRight,
  Check,
  Bot,
  Brain,
  Shield,
  Clock,
  Zap,
  BarChart3,
  Building2,
  Briefcase,
  Headphones,
  Globe,
  Lock,
  TrendingUp,
  Server,
  Users,
  MessageSquare,
  Cpu,
} from 'lucide-react';

const stats = [
  { label: 'Agents included', value: 'Unlimited' },
  { label: 'Uptime SLA', value: '99.9%' },
  { label: 'Avg response time', value: '<1s' },
  { label: 'Languages supported', value: '50+' },
];

const features = [
  {
    icon: MessageSquare,
    title: 'Autonomous ticket resolution',
    text: 'Zyra can read, understand, and resolve common tickets end-to-end across chat, email, and voice channels.',
  },
  {
    icon: Brain,
    title: 'Natural language context',
    text: 'Zyra keeps full conversation history in context, so customers never have to repeat themselves.',
  },
  {
    icon: Shield,
    title: 'Brand-safe tone controls',
    text: 'Set language rules, topics, escalation triggers, and guardrails so Zyra speaks like your team.',
  },
  {
    icon: Zap,
    title: 'Premium AI models',
    text: 'Trained on leading AI models, not fine-tuned on your private data. Your data never trains shared models.',
  },
  {
    icon: Globe,
    title: 'Multi-channel presence',
    text: 'Works inside the Zuri Hub inbox, widget, email, and Zuri Voice so one AI covers every channel.',
  },
  {
    icon: BarChart3,
    title: 'Enterprise analytics',
    text: 'Track resolution rates, handoff times, customer satisfaction, and agent productivity by team or region.',
  },
];

const whyEnterprise = [
  {
    icon: TrendingUp,
    title: 'Scale without headcount',
    text: 'Let Zyra deflect and resolve the repetitive work so your team can focus on high-value relationships.',
  },
  {
    icon: Clock,
    title: '24/7 consistent coverage',
    text: 'Customers get instant, on-brand answers at any hour, in any language your team supports.',
  },
  {
    icon: Shield,
    title: 'Governance first',
    text: 'Built-in guardrails, audit logs, and escalation paths make AI safe for regulated and global businesses.',
  },
  {
    icon: Lock,
    title: 'Data never leaves your control',
    text: 'Private cloud, dedicated instance, and workspace-isolated processing keep your data protected.',
  },
];

const industries = [
  { icon: Building2, title: 'Financial services', text: 'Handle account, payment, and compliance queries with strict guardrails and full audit trails.' },
  { icon: Headphones, title: 'Telecoms', text: 'Resolve billing, plan, and outage questions across chat, voice, and email without channel switching.' },
  { icon: Briefcase, title: 'B2B SaaS', text: 'Onboard users, answer technical questions, and escalate to CS when expansion signals appear.' },
  { icon: Briefcase, title: 'Healthcare', text: 'Triage non-clinical requests with privacy controls, escalation, and role-based permissions.' },
  { icon: Globe, title: 'E-commerce', text: 'Manage order, return, and shipping tickets at scale during seasonal demand spikes.' },
  { icon: Users, title: 'Education', text: 'Support students, staff, and parents across time zones with natural, helpful answers.' },
];

const useCases = [
  { title: 'Overflow deflection', text: 'Zyra resolves common FAQs and order questions instantly, reserving human agents for the complex cases.' },
  { title: 'First-line support', text: 'New agents get real-time coaching and draft replies that match your brand and policy.' },
  { title: 'Global expansion', text: 'Deploy the same AI agent across languages and regions without duplicating headcount.' },
  { title: 'Voice to ticket handoff', text: 'Zyra transcribes, routes, and opens tickets from Zuri Voice calls with full context attached.' },
];

const pricing = [
  { label: 'Per-token charges', value: 'None' },
  { label: 'Per-resolution charges', value: 'None' },
  { label: 'AI usage', value: 'Included' },
  { label: 'Zyra Agents', value: 'Unlimited' },
  { label: 'Channels', value: 'All included' },
  { label: 'Billing model', value: 'One annual price' },
];

const faqs = [
  {
    q: 'How many Zyra Agents can an Enterprise workspace run?',
    a: 'Enterprise includes unlimited Zyra Agents. You can deploy agents per channel, department, brand, or region without worrying about per-agent seat limits.',
  },
  {
    q: 'Is pricing per ticket, per token, or per resolution?',
    a: 'No. Zyra Agent is included in the Enterprise plan at one flat annual price. There are no per-token, per-message, or per-resolution charges.',
  },
  {
    q: 'Can Zyra handle voice calls?',
    a: 'Yes. Zyra works with Zuri Voice to transcribe calls, answer common questions, and hand complex calls to a human agent.',
  },
  {
    q: 'What AI models power Zyra Agent?',
    a: 'Zyra is built on premium, state-of-the-art AI models. Your Customer Data is never used to train shared or general-purpose models.',
  },
  {
    q: 'How do I control what Zyra can and cannot say?',
    a: 'Owners and admins can set tone, language rules, allowed topics, and automatic escalation paths from the workspace settings.',
  },
  {
    q: 'What uptime is available?',
    a: 'Zyra Agent is backed by a 99.9% uptime SLA with proactive monitoring and a published incident response process.',
  },
];

export default function ZyraAgent() {
  return (
    <>
      <SEO
        title="Zyra Agent"
        description="Zyra Agent is the Enterprise AI that resolves tickets, maintains context, and scales with one flat price. No per-token or per-resolution charges."
        path="/zyra-agent"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            <Sparkles className="h-4 w-4" /> Enterprise AI
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Meet <span className="gradient-text">Zyra Agent</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            An autonomous Enterprise AI that resolves tickets, keeps full conversation context, and runs every channel under one flat price. No per-token or per-resolution charges.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="/book-a-demo"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
            >
              Book a demo <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/60 px-7 py-3.5 text-base font-bold text-slate-200 transition hover:bg-slate-800"
            >
              See Enterprise pricing
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16" aria-labelledby="stats-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-4">
            {stats.map((s) => (
              <div
                key={s.label}
                className="flex flex-col items-center justify-center rounded-2xl border border-slate-100 bg-slate-50 p-6 text-center transition hover:-translate-y-1 hover:shadow-lg"
              >
                <p className="text-3xl font-extrabold text-slate-900">{s.value}</p>
                <p className="mt-1 text-sm font-semibold text-slate-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="what-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-brand-200 bg-brand-50 p-8 text-center shadow-sm">
            <h2 id="what-heading" className="text-2xl font-bold text-slate-900">
              What is Zyra Agent?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-700">
              Zyra Agent is an Enterprise AI assistant that can hold full conversations, keep natural-language context, and resolve tickets without per-token or per-resolution fees. It runs on premium AI models and is governed by your tone, escalation, and security rules.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="why-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="why-heading" className="section-title">Why Enterprise needs Zyra Agent</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Enterprises choose Zyra to scale support without scaling costs, keep quality consistent, and stay in control.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyEnterprise.map((w) => (
              <div
                key={w.title}
                className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-xl shadow-brand-900/5 transition duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-900/10"
              >
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-900/20 transition group-hover:scale-110">
                  <w.icon className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{w.title}</h3>
                <p className="mt-2 text-slate-600">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="features-heading" className="section-title">Everything Zyra can do</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              One AI with the capabilities Enterprise support teams actually need.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="industries-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="industries-heading" className="section-title text-white">Adapts to every industry</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl text-slate-400">
              Zyra learns your terminology, policies, and workflows so it fits naturally into any sector.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {industries.map((i) => (
              <div
                key={i.title}
                className="rounded-3xl border border-slate-700 bg-slate-800 p-8 transition hover:-translate-y-1 hover:bg-slate-750"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-600 text-white">
                  <i.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-white">{i.title}</h3>
                <p className="mt-2 text-slate-300">{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="usecases-heading" className="section-title">Real use cases</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              See how Enterprise teams put Zyra to work.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {useCases.map((u) => (
              <div
                key={u.title}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-xl shadow-brand-900/5 transition hover:-translate-y-1 hover:shadow-2xl hover:shadow-brand-900/10"
              >
                <h3 className="text-lg font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="context-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="context-heading" className="section-title">Natural language context</h2>
              <p className="mt-4 text-lg text-slate-600">
                Zyra does not treat each message as an isolated prompt. It keeps the full thread, customer record, and previous resolutions in mind so customers feel understood, not processed.
              </p>
              <ul className="mt-8 space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  <span className="text-slate-700">Full conversation memory across channels</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  <span className="text-slate-700">Customer history and preferences in every reply</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  <span className="text-slate-700">Multi-turn reasoning for complex requests</span>
                </li>
              </ul>
            </div>
            <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-600">
                  <Bot className="h-5 w-5" />
                </div>
                <span className="font-bold">Zyra Agent</span>
              </div>
              <div className="mt-4 space-y-3">
                <div className="rounded-2xl bg-slate-800 p-4 text-sm text-slate-300">
                  Customer: "I still have not received the replacement you promised on Monday."
                </div>
                <div className="rounded-2xl bg-brand-600 p-4 text-sm text-white">
                  Zyra: "I see the replacement for order #4589 was scheduled Monday but has not shipped. I have escalated it to your support queue with priority. You will get an update within the hour."
                </div>
                <div className="flex gap-2 text-sm">
                  <span className="rounded-full bg-green-500/20 px-3 py-1 text-green-300">Context: order #4589</span>
                  <span className="rounded-full bg-amber-500/20 px-3 py-1 text-amber-300">Escalated</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="models-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="models-heading" className="section-title">Trained on premium AI models</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Zyra runs on premium, secure AI models. Your data is isolated to your workspace and never used to train shared models.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            <div className="rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-sm">
              <Cpu className="mx-auto h-10 w-10 text-brand-600" />
              <h3 className="mt-4 text-lg font-bold text-slate-900">Premium models</h3>
              <p className="mt-2 text-slate-600">Built on leading foundation models and kept up to date without retraining on your data.</p>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-sm">
              <Shield className="mx-auto h-10 w-10 text-brand-600" />
              <h3 className="mt-4 text-lg font-bold text-slate-900">Data isolation</h3>
              <p className="mt-2 text-slate-600">Customer Data stays in your workspace. No shared model training, no cross-customer leakage.</p>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-sm">
              <Server className="mx-auto h-10 w-10 text-brand-600" />
              <h3 className="mt-4 text-lg font-bold text-slate-900">Private cloud ready</h3>
              <p className="mt-2 text-slate-600">Choose dedicated or private cloud infrastructure for full data residency.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="bg-slate-900 py-24 text-white" aria-labelledby="pricing-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="pricing-heading" className="section-title text-white">Enterprise pricing for Zyra Agent</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl text-slate-400">
              Everything under one flat annual price. No per-token charges. No per-resolution fees. No surprise bills.
            </p>
          </div>
          <div className="mt-14 overflow-hidden rounded-3xl border border-slate-700 bg-slate-800">
            <div className="grid gap-6 p-8 sm:grid-cols-2 lg:grid-cols-3">
              {pricing.map((p) => (
                <div key={p.label} className="flex items-center justify-between rounded-2xl bg-slate-900/50 p-4 sm:block">
                  <p className="font-semibold text-slate-300">{p.label}</p>
                  <p className="text-lg font-bold text-white sm:mt-1">{p.value}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-700 p-8">
              <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
                <div>
                  <p className="text-2xl font-extrabold text-white">Custom Enterprise quote</p>
                  <p className="text-slate-400">Includes unlimited Zyra Agents, all channels, dedicated onboarding, and a 99.9% uptime SLA.</p>
                </div>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-8 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
                >
                  Get a quote <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="sla-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="sla-heading" className="section-title">Scalability and uptime</h2>
              <p className="mt-4 text-lg text-slate-600">
                Zyra is served from the same global edge as Zuri Hub. It scales automatically with demand and is backed by an Enterprise SLA.
              </p>
              <ul className="mt-8 space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  <span className="text-slate-700">99.9% uptime SLA with published incident response</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  <span className="text-slate-700">Auto-scaling inference across regions</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  <span className="text-slate-700">Rollback plan for every model update</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  <span className="text-slate-700">Health checks and monitoring included</span>
                </li>
              </ul>
            </div>
            <div className="rounded-3xl bg-slate-50 p-8">
              <h3 className="text-xl font-bold text-slate-900">Enterprise support included</h3>
              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Headphones className="mt-0.5 h-5 w-5 text-brand-600" />
                  <div>
                    <p className="font-bold text-slate-900">Dedicated success manager</p>
                    <p className="text-sm text-slate-600">A single point of contact for onboarding, tuning, and expansion.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="mt-0.5 h-5 w-5 text-brand-600" />
                  <div>
                    <p className="font-bold text-slate-900">Multi-region deployment</p>
                    <p className="text-sm text-slate-600">Data residency and low-latency inference where your customers are.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lock className="mt-0.5 h-5 w-5 text-brand-600" />
                  <div>
                    <p className="font-bold text-slate-900">Security and compliance</p>
                    <p className="text-sm text-slate-600">SSO, SCIM, audit logs, and private cloud options are available.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-100 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Ready to add Zyra to your Enterprise?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Book a demo to see how Zyra Agent can scale your support, keep full context, and protect your brand.
          </p>
          <a
            href="/book-a-demo"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Book a demo <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
